import React from "react";
import { __ } from "@wordpress/i18n";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import Panel from "../../../components/common/Panel";
import {
	ToggleButton,
	ToggleButtonGroup,
	PopoverDrawer,
	ColorPicker,
	IconPicker,
	Slider,
	Typography,
} from "../../../controls";
import {
	AlignCenter,
	AlignLeft,
	AlignRight,
} from "../../../components/icons/Icons";
import ZStack from "../../../components/common/ZStack";
import {
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	position,
} from "@chakra-ui/react";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			icon,
			iconGap,
			iconSize,
			iconColor,
			dateTypography,
			alignment,
			color,
			blockPadding,
			blockMargin,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
		},
		setAttributes,
	} = props;

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<ToggleButtonGroup
						value={alignment}
						onChange={(value) => {
							setAttributes({ alignment: value });
						}}
						label={__("Alignment", "magazine-blocks")}
						responsive
						resetAttributeKey="alignment"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<>
						<IconPicker
							value={icon}
							onChange={(val) => setAttributes({ icon: val })}
						/>
						<Slider
							label={__("Size", "magazine-blocks")}
							min={0}
							max={50}
							value={iconSize}
							onChange={(val: any) =>
								setAttributes({ iconSize: val })
							}
							responsive
							units={["px", "em", "%"]}
						/>
						<Slider
							label={__("Gap", "magazine-blocks")}
							min={0}
							max={60}
							value={iconGap}
							onChange={(val: any) =>
								setAttributes({ iconGap: val })
							}
							responsive
							units={["px", "em", "%"]}
						/>
					</>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel
					title={__("Text and Date", "magazine-blocks")}
					initialOpen
				>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: color,
											id: "color",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) =>
											setAttributes({ color: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									></ColorPicker>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={dateTypography}
						onChange={(val) =>
							setAttributes({
								dateTypography: val,
							})
						}
					/>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: iconColor,
											id: "iconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconColor}
										resetKey="iconColor"
										onReset={(v) =>
											setAttributes({ iconColor: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={iconColor}
										onChange={(v) =>
											setAttributes({ iconColor: v })
										}
									></ColorPicker>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
